import _ from "underscore";
import DriveView from "../../DriveView";
import Messages from "../../model/Messages";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import Message from "../../model/Message";
import { PageLevel } from "../../store/mobile/types";
import { getCurrentVehicle } from "../../utils/device";
import ButtonsBar from "../framework/buttonsBar";
import Api from "../../services/api";
import { BaseCache, makeDeviceCache, makeUserCache } from "../../utils/storage/cache";
import { Device } from "../../model/Device";
import { NODEVICEID, NOUSERID } from "../../model/DutyStatusLog";
import { DriveStore } from "../../store/reducers";
import Notifications from "../../utils/notifications";
import { MobileActions } from "../../store/mobile/actions";
import { handleApi } from "../../utils/error";
import {hashToArray, htmlEscape} from "../../utils/vanilla";
import pageHTML from "./main.tmpl";
import { isSystemMessage } from "./helpers";

export default class MessagesView extends DriveView<any> {

    api: Api;
    currentVehicle: Device;
    store: DriveStore;

    protected strings = [
        "Send a message"
    ];
    private readonly template = _.template(pageHTML);
    private readonly messages: Messages;
    private readonly deviceCache: BaseCache<IDevice | Device>;
    private readonly userCache;
    private users;
    private currentUser: DeepReadonly<IUser> | null;
    private buttonsBar: ButtonsBar | undefined;

    constructor (options) {
        super(options, {
            title: "Messages",
            activeSection: "messaging",
            activeView: "inbox",
            pageLevel: PageLevel.ONE
        });
        this.api = this.app.api;
        this.store = this.app.store;
        this.deviceCache = makeDeviceCache(this.store);
        this.userCache = makeUserCache(this.store);
        this.users = this.userCache.getDict();
        this.currentUser = this.app.userService.getCurrentUser();

        this.messages = new Messages(this.app.store, this.currentUser?.id);
        this.messages.fetch();

        this.listenTo(dispatcher, DispatcherEvents.MessagesChange, () => {
            this.users = this.userCache.getDict();
            this.messages.fetch();
            this.render();
        });
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .message-wrapper__content": this.viewMessage,
            "click #compose": this.composeMessage
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        this.showSendAMessageButton();
        this.currentUser = this.app.userService.getCurrentUser();
        let currentVehicleId = getCurrentVehicle();

        let updateCurrentVehicle = (): Promise<void> => {
            return new Promise((resolve, reject) => {
                if (currentVehicleId) {
                    this.deviceCache.getOrFetch(this.api, { id: currentVehicleId.id }).then(devices => {
                        this.currentVehicle = new Device(devices && devices[0] && devices[0][0]);
                        resolve();
                    }).catch(reject);
                } else {
                    reject(new Error("No current vehicle"));
                }
            });
        };

        let allMessages = this.messages.getMessages({
            sortAscending: false,
            groupRouteMessages: true
        });

        let deviceMessages: Message[] = [];
        let systemMessages: Message[] = [];
        let allRecipientMessages: Message[] = [];

        const isCurrentDeviceMessage = (msg: Message) => {
            return msg.device?.id !== NODEVICEID && msg.device?.id === currentVehicleId?.id;
        };

        this.clearUnReadNotifications(allMessages);

        allMessages.forEach((mes) => {
            if (isCurrentDeviceMessage(mes)) {
                if (isSystemMessage(mes)) {
                    systemMessages.push(mes);
                } else {
                    deviceMessages.push(mes);
                }
            } else if (mes.recipient && mes.recipient?.id !== NOUSERID) {
                allRecipientMessages.push(mes);
            }
        });

        this.populateRoutes(deviceMessages);
        let deviceGroupedMessagesAddressesLookup: IIdDictionary<string[]> = this.getGroupedMessagesAddressesLookup(deviceMessages);

        this.populateRoutes(allRecipientMessages);
        let recipientGroupedMessagesAddressesLookup: IIdDictionary<string[]> = this.getGroupedMessagesAddressesLookup(allRecipientMessages);

        let latestDeviceMessage = deviceMessages && deviceMessages[0];
        let latestSystemMessage = systemMessages && systemMessages[0];
        let recipientChatsDict = this.groupRecipientConversations(allRecipientMessages);

        let latestRecipientChats: Message[] = [];

        let renderImpl = () => {
            void this.display(this.template({
                deviceMessage: latestDeviceMessage,
                deviceGroupedMessagesAddressesLookup: deviceGroupedMessagesAddressesLookup,
                recipientGroupedMessagesAddressesLookup: recipientGroupedMessagesAddressesLookup,
                recipientMessages: latestRecipientChats,
                currentVehicle: this.currentVehicle,
                currentUser: this.currentUser,
                currentUserFirstName: this.currentUser?.firstName ? htmlEscape(this.currentUser.firstName) : "",
                currentUserLastName: this.currentUser?.lastName ? htmlEscape(this.currentUser.lastName) : "",
                currentVehicleName: htmlEscape(this.currentVehicle.name),
                systemMessage: latestSystemMessage
            }));
        };

        let renderUI = () => {
            if (!this.currentVehicle) {
                updateCurrentVehicle()
                    .then(renderImpl)
                    .catch(handleApi);
            } else {
                renderImpl();
            }
        };

        let missingUsersInCache = this.getMissingUsersInUserCache(recipientChatsDict);

        if (Object.keys(missingUsersInCache).length > 0) {
            this.updateMissingUsers(missingUsersInCache)
                .then(() => {
                    this.updateSenderNames(recipientChatsDict, latestRecipientChats);
                    renderUI();
                })
                .catch(handleApi);
        } else {
            this.updateSenderNames(recipientChatsDict, latestRecipientChats);
            renderUI();
        }
        return this;
    }

    getMissingUsersInUserCache = (recipientChatsDict: IIdDictionary<Message[]>) => {
        let missingUsers = {};
        Object.keys(recipientChatsDict).forEach(key => {
            if (recipientChatsDict[key].length > 0) {
                let latestRecipientChat = recipientChatsDict[key][0];
                let user = latestRecipientChat.user;
                let recipient = latestRecipientChat.recipient;

                if (user && user.id !== NOUSERID) {
                    if (user.id === this.currentUser?.id && recipient && recipient.id !== NOUSERID && !this.users[recipient.id]) {
                        missingUsers[recipient.id] = recipient.id;
                    } else if (!this.users[user.id]) {
                        missingUsers[user.id] = user.id;
                    }
                }
            }
        });
        return missingUsers;
    };

    updateMissingUsers = (missingUsers) => {
        return this.userCache.getOrFetch(this.api, hashToArray<IIdEntity>(missingUsers).map(userId => ({ id: userId })));
    };

    updateSenderNames = (recipientChatsDict: IIdDictionary<Message[]>, latestRecipientChats: Message[]) => {
        // populate admin name of each latest recipient conversation
        const defaultSenderName = "Administrator";
        Object.keys(recipientChatsDict).forEach(key => {
            if (recipientChatsDict[key].length > 0) {
                let latestRecipientChat = recipientChatsDict[key][0];
                let user = latestRecipientChat.user;
                let recipient = latestRecipientChat.recipient;

                // Message that user sent to themselves should show up as conversation with admin
                if (key === this.currentUser?.id && recipient && recipient.id !== NOUSERID && key === recipient.id) {
                    latestRecipientChat._userName = defaultSenderName;
                    latestRecipientChats.push(latestRecipientChat);
                    return;
                }
                if (user?.id !== NOUSERID) {
                    let senderId = user?.id;
                    if (senderId === this.currentUser?.id && recipient?.id !== NOUSERID) {
                        senderId = recipient?.id;
                    }
                    let fetchedUser = senderId && this.users[senderId];
                    let fetchedUserName = defaultSenderName;
                    if (fetchedUser) {
                        fetchedUserName = `${ fetchedUser.firstName } ${ fetchedUser.lastName }`;
                    }

                    latestRecipientChat._userName = fetchedUserName;
                    let foundIndex = latestRecipientChats.indexOf(latestRecipientChat);
                    if (foundIndex !== -1) {
                        latestRecipientChats[foundIndex] = latestRecipientChat;
                    } else {
                        latestRecipientChats.push(latestRecipientChat);
                    }
                }
            }
        });
    };

    showSendAMessageButton() {
        this.buttonsBar && this.buttonsBar.hide();
        this.buttonsBar = new ButtonsBar([{
            title: this.getString("Send a message"),
            id: `compose`,
            clickHandler: () => {
                this.composeMessage();
            }
        }], {
            isNightMode: this.app.store.getState().data.nightMode
        });

        this.buttonsBar.show();
    }

    clearUnReadNotifications = (allMessages: Message[]) => {
        if (allMessages.length > 0) {
            const isMsgUnRead = (msg: Message) => !msg.read && (typeof (msg.user) === "undefined" || (msg.user?.id !== NOUSERID && msg.user.id !== this.currentUser?.id));
            let hasUnreadMessages = allMessages.some(isMsgUnRead);
            if (!hasUnreadMessages && this.store.getState().mobile.newMessage) {
                this.currentUser && Notifications.remove("unreadMessages", this.currentUser?.id);
                this.store.dispatch(MobileActions.setNewMessage(hasUnreadMessages));
            }
        }
    };

    groupRecipientConversations = (allRecipientMessages: Message[]) => {
        let recipientChatsDict: IIdDictionary<Message[]> = {};
        const isRecipientLoggedInUser = (recipient) => {
            return recipient && recipient?.id === this.currentUser?.id;
        };
        const isLoggedInUserSender = (user) => {
            return user && user?.id === this.currentUser?.id;
        };
        allRecipientMessages.forEach(message => {
            let recipient = message.recipient;
            let user = message.user;
            if (isRecipientLoggedInUser(recipient) && user && user?.id !== NOUSERID) {
                if (!recipientChatsDict[user.id]) {
                    recipientChatsDict[user.id] = [];
                }
                recipientChatsDict[user.id].push(message);
            }
            if (isLoggedInUserSender(user) && recipient && recipient?.id !== NOUSERID) {
                if (!recipientChatsDict[recipient.id]) {
                    recipientChatsDict[recipient.id] = [];
                }
                recipientChatsDict[recipient.id].push(message);
            }
        });
        return recipientChatsDict;
    };

    populateRoutes(deviceMessages: Message[]) {
        let apiUser = this.api.getUser();
        if (!apiUser) {
            return;
        }
        let routes = this.store.getState().routes[apiUser.id] || {};
        if (!routes) {
            return;
        }
        deviceMessages.forEach(message => {
            let content = message.messageContent;
            if (content?.routeId && routes && routes.hasOwnProperty(content.routeId)) {
                message._routeName = routes[content.routeId].name;
            }
        });
    }

    getGroupedMessagesAddressesLookup = (deviceMessages: Message[]) => {
        return deviceMessages.reduce((messageDict, message) => {
            if (message._groupedMessages) {
                let addresses = message._groupedMessages.map(m => m.messageContent?.address || "");
                if (addresses.length > 3) {
                    addresses = [...addresses.slice(0, 1), "...", ...addresses.slice(-1)];
                }
                messageDict[message.id] = addresses;
            }
            return messageDict;
        }, {} as IIdDictionary<string[]>);
    };

    viewMessage (evt) {
        let id = evt.currentTarget.dataset.id;
        let type = evt.currentTarget.dataset.type;
        evt.preventDefault();
        if (id) {
            this.app.router.navigate(`messaging/view,(id:'${ id }',type:'${ type }')`, true);
        } else {
            this.app.router.navigate(`messaging/view,(type:'${ type }')`, true);
        }
    }

    composeMessage () {
        let isChronological = false;
        let existingMessages = this.messages.getMessages({
            sortAscending: isChronological,
            groupRouteMessages: true
        });
        // filter out system message and device message
        let recipientMessages = existingMessages.filter(msg => msg.device?.id === NODEVICEID).sort(Messages.messageComparator(isChronological));

        if (recipientMessages.length === 0) {
            this.app.router.navigate(`messaging/view,(type:'recipient')`, true);
        } else {
            let latestRecipientMsg = recipientMessages[0];
            let senderId = (latestRecipientMsg.user as IIdEntity).id;
            let recipientId = (latestRecipientMsg.recipient as IIdEntity).id;
            let id = this.currentUser?.id !== senderId ? senderId : recipientId;
            this.app.router.navigate(`messaging/view,(id:'${ id }',type:'recipient')`, true);
        }
    }

    remove() {
        this.buttonsBar && this.buttonsBar.hide();
        delete this.buttonsBar;
        super.remove();
        return this;
    }
}